class CollectionStickyShell {
  /**
   * @param {HTMLElement} host
   */
  constructor(host) {
    this.host = host;
    this.glass = null;
    this.shell = null;
    this.titleSlot = null;
    this.filterSlot = null;
    this.mediaQuery = window.matchMedia('(min-width: 0px)');
    this.rafId = 0;
    this.dockStart = 0;

    this.title = null;
    this.filterTrigger = null;
    this.facetsToggle = null;
    this.mobileWrapper = null;

    this.titlePlaceholder = null;
    this.filterPlaceholder = null;
    this.boundFilterTrigger = null;

    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.update = this.update.bind(this);

    this.resolveElements();

    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.mediaQuery.addEventListener('change', this.handleMediaChange);
    window.addEventListener('load', this.handleResize, { once: true });

    this.measure();
  }

  disconnect() {
    this.restoreDesktopLayout();
    this.unbindFilterTrigger();
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    this.host.style.removeProperty('--collection-sticky-shell-height');
    this.host.style.removeProperty('--collection-sticky-glass-height');
    this.host.classList.remove('is-docked');
  }

  resolveHostElements() {
    this.glass = this.host.querySelector('[data-collection-sticky-glass]');
    this.shell = this.host.querySelector('[data-collection-sticky-shell]');
    this.titleSlot = this.host.querySelector('[data-collection-sticky-title-slot]');
    this.filterSlot = this.host.querySelector('[data-collection-sticky-filter-slot]');
  }

  resolveElements() {
    this.resolveHostElements();

    if (this.titlePlaceholder && !this.titlePlaceholder.isConnected) {
      this.titlePlaceholder = null;
    }

    if (this.filterPlaceholder && !this.filterPlaceholder.isConnected) {
      this.filterPlaceholder = null;
    }

    this.title = document.querySelector('.text-block--role-page-title');
    this.filterTrigger = document.querySelector('.facets__filter-sort-trigger--mobile');
    this.facetsToggle = document.querySelector('.template-collection-layout .facets-toggle');
    this.mobileWrapper = this.facetsToggle?.querySelector('.facets-mobile-wrapper') ?? null;

    if (this.title instanceof HTMLElement && !this.titlePlaceholder && this.title.parentNode) {
      this.titlePlaceholder = document.createComment('collection-sticky-title-placeholder');
      this.title.parentNode.insertBefore(this.titlePlaceholder, this.title);
    }

    if (
      this.filterTrigger instanceof HTMLElement &&
      !this.filterPlaceholder &&
      this.filterTrigger.parentNode
    ) {
      this.filterPlaceholder = document.createComment('collection-sticky-filter-placeholder');
      this.filterTrigger.parentNode.insertBefore(this.filterPlaceholder, this.filterTrigger);
    }

    this.bindFilterTrigger();
  }

  bindFilterTrigger() {
    if (this.boundFilterTrigger === this.filterTrigger) return;

    this.unbindFilterTrigger();

    if (this.filterTrigger instanceof HTMLElement) {
      this.filterTrigger.addEventListener('click', this.handleFilterClick);
      this.boundFilterTrigger = this.filterTrigger;
    }
  }

  unbindFilterTrigger() {
    if (this.boundFilterTrigger instanceof HTMLElement) {
      this.boundFilterTrigger.removeEventListener('click', this.handleFilterClick);
    }

    this.boundFilterTrigger = null;
  }

  handleFilterClick() {
    const filtersDrawer = document.getElementById('filters-drawer');
    const dialog = filtersDrawer?.querySelector('dialog');

    if (
      filtersDrawer &&
      typeof filtersDrawer.showDialog === 'function' &&
      dialog instanceof HTMLDialogElement &&
      !dialog.open
    ) {
      filtersDrawer.showDialog();
    }
  }

  handleResize() {
    this.measure();
  }

  handleMediaChange() {
    this.measure();
  }

  handleScroll() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = 0;
      this.update();
    });
  }

  measure() {
    this.resolveElements();

    if (
      !(this.shell instanceof HTMLElement) ||
      !(this.titleSlot instanceof HTMLElement) ||
      !(this.filterSlot instanceof HTMLElement)
    ) {
      return;
    }

    if (!this.mediaQuery.matches) {
      this.restoreDesktopLayout();
      this.host.style.setProperty(
        '--collection-sticky-shell-height',
        `${Math.ceil(this.shell.offsetHeight)}px`
      );
      this.host.style.setProperty('--collection-sticky-glass-height', '0px');
      this.host.classList.remove('is-docked');
      return;
    }

    this.adoptMobileLayout();

    const shellHeight = Math.ceil(this.shell.offsetHeight || 0);
    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const hostTop = this.host.getBoundingClientRect().top + window.scrollY;

    this.host.style.setProperty('--collection-sticky-shell-height', `${shellHeight}px`);
    this.dockStart = Math.max(0, Math.round(hostTop - headerHeight));
    this.update();
  }

  adoptMobileLayout() {
    if (!(this.title instanceof HTMLElement) || !(this.filterTrigger instanceof HTMLElement)) {
      return;
    }

    if (this.title.parentNode !== this.titleSlot) {
      this.titleSlot.appendChild(this.title);
    }

    if (this.filterTrigger.parentNode !== this.filterSlot) {
      this.filterSlot.appendChild(this.filterTrigger);
    }

    this.facetsToggle?.setAttribute('data-mobile-trigger-detached', '');
  }

  restoreDesktopLayout() {
    if (this.title instanceof HTMLElement && this.titlePlaceholder?.parentNode) {
      this.titlePlaceholder.parentNode.insertBefore(this.title, this.titlePlaceholder.nextSibling);
    }

    if (this.filterTrigger instanceof HTMLElement) {
      if (this.filterPlaceholder?.parentNode) {
        this.filterPlaceholder.parentNode.insertBefore(
          this.filterTrigger,
          this.filterPlaceholder.nextSibling
        );
      } else if (
        this.mobileWrapper instanceof HTMLElement &&
        !this.mobileWrapper.contains(this.filterTrigger)
      ) {
        this.mobileWrapper.appendChild(this.filterTrigger);
      }
    }

    this.facetsToggle?.removeAttribute('data-mobile-trigger-detached');
  }

  update() {
    if (!this.mediaQuery.matches) {
      this.host.classList.remove('is-docked');
      this.host.style.setProperty('--collection-sticky-glass-height', '0px');
      return;
    }

    const scrollY = window.scrollY;
    const isDocked = scrollY >= this.dockStart;
    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const shellHeight = Math.ceil(this.shell?.getBoundingClientRect().height || 0);

    this.host.classList.toggle('is-docked', isDocked);
    this.host.style.setProperty(
      '--collection-sticky-glass-height',
      isDocked ? `${Math.ceil(headerHeight + shellHeight)}px` : '0px'
    );
  }
}

const collectionStickyShellInstances = new WeakMap();

/**
 * @param {ParentNode} root
 */
function initCollectionStickyShells(root = document) {
  const hosts = root.querySelectorAll('[data-collection-sticky-shell-host]');

  hosts.forEach((host) => {
    if (!(host instanceof HTMLElement)) return;

    const instance = collectionStickyShellInstances.get(host);

    if (instance instanceof CollectionStickyShell) {
      instance.measure();
      return;
    }

    collectionStickyShellInstances.set(host, new CollectionStickyShell(host));
  });
}

let stickyShellRefreshFrame = 0;

function refreshCollectionStickyShells() {
  if (stickyShellRefreshFrame) return;

  stickyShellRefreshFrame = window.requestAnimationFrame(() => {
    stickyShellRefreshFrame = 0;
    initCollectionStickyShells();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initCollectionStickyShells();
    },
    { once: true }
  );
} else {
  initCollectionStickyShells();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initCollectionStickyShells(event.target);
});

const collectionStickyShellObserver = new MutationObserver((mutations) => {
  const shouldRefresh = mutations.some((mutation) => {
    if (mutation.type !== 'childList') return false;

    if (
      mutation.target instanceof HTMLElement &&
      (mutation.target.closest('.template-collection-layout, [data-collection-sticky-shell-host]') ||
        mutation.target.querySelector('.template-collection-layout, [data-collection-sticky-shell-host]'))
    ) {
      return true;
    }

    return Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof HTMLElement)) return false;

      return (
        node.matches('[data-collection-sticky-shell-host], .template-collection-layout') ||
        Boolean(node.querySelector('[data-collection-sticky-shell-host], .template-collection-layout'))
      );
    });
  });

  if (shouldRefresh) {
    refreshCollectionStickyShells();
  }
});

function observeCollectionStickyShells() {
  if (!document.body) return;

  collectionStickyShellObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

if (document.body) {
  observeCollectionStickyShells();
} else {
  document.addEventListener('DOMContentLoaded', observeCollectionStickyShells, { once: true });
}
